import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import Table from '../../../components/Table/Table';
import { connect } from 'react-redux';
import constants from '../../../utils/constants';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import templateInstructions from '../../../utils/templateInstructions';

function AlertTemplates(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.aPlusEdit)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('alertName');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if all template instructions are valid
  const validTemplateIds = new Set(templateInstructions.map(instruction => instruction.id));

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const loadTable = async (map, resetSelection = true) => {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const templateResponse = await API.getAlertPlusTemplates();

      const instructionsMap = templateInstructions.reduce((acc, instruction) => {
        acc[instruction.id] = instruction.instructions;
        return acc;
      }, {});

      const templates = templateResponse.data
        .filter(template => validTemplateIds.has(template.id))  // Filter based on valid template IDs
        .map(template => ({
          accountId: template.account_id,
          backgroundColor: template.background_color,
          duration: template.duration,
          icon: template.icon,
          id: template.id,
          image: template.image,
          name: template.name,
          preset: template.preset,
          sound: template.sound,
          instructions: instructionsMap[template.id] || '',
          checked: false,
        }));

      console.log('templates:', templates);

      templates.forEach(template => {
        map.data[template.id] = template;
      });

      map.ids = templates.map(template => template.id);
      map.filteredIds = map.ids;
      sortMap(map);
      setData(generateDataSlice(map));
      setDataCount(map.sortedIds.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (error) {
      setError('Error fetching templates');
      console.error('Error fetching templates:', error);
      setDataLoaded(true);
    }
  };

  const dataMap = React.useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [] };
    loadTable(map);
    return map;
  }, []);

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 20,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  useEffect(() => {
    loadTable(dataMap);
  }, []);

  const tableColumns = React.useMemo(() => [
    {
      Header: 'ICON',
      accessor: 'icon',
      id: 'icon',
      isVisible: true,
    },
    {
      Header: 'TEMPLATE',
      accessor: 'name',
      id: 'name',
      isVisible: true,
    },
    {
      Header: 'INSTRUCTIONS',
      accessor: 'instructions',
      id: 'instructions',
      isVisible: true,
    },
  ], []);

  const tableProps = {
    pageType: constants.pageType.Templates,
    modalId: null,
    columns: tableColumns,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
  };

  return (
    <div>
      <Table {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(AlertTemplates);
